import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, isAdmin, canManageEmployees, canUseApp } from './AuthContext';

/** ログイン画面へ送る。ログインしたら「開こうとしていた画面」に戻す。
 *  どの入口（管理者専用・アプリ別・設定）でも同じ動き。 */
function LoginRedirect() {
  const location = useLocation();
  const next = location.pathname + location.search;
  const to = next.startsWith('/login') ? '/login' : `/login?next=${encodeURIComponent(next)}`;
  return <Navigate to={to} replace />;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="w-10 h-10 rounded-full border-4 border-zinc-200 border-t-[#0052FF] animate-spin" />
    </div>
  );
}

export function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  if (user && typeof user === 'object' && user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function NotAuthorized({ title = '管理者専用ページ' }) {
  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center gap-2 p-6 text-center">
      <h1 className="text-2xl font-bold text-zinc-900">{title}</h1>
      <p className="text-sm text-zinc-500">このページにアクセスする権限がありません。</p>
    </div>
  );
}

export function RequireAdmin({ children, redirectTo }) {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  if (!isAdmin(user)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return <NotAuthorized />;
  }
  return children;
}

/** App-access gate — redirects to the launcher if the role can't open this app. */
export function RequireApp({ app, children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  if (!canUseApp(user, app)) return <Navigate to="/" replace />;
  return children;
}

/** Settings area gate — any authenticated user may enter; individual settings
 * pages are guarded by their own capability/admin checks. */
export function RequireSettingsAccess({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  return children;
}

/** Capability gate for a specific settings page (e.g. 署名・捺印). */
export function RequireCapability({ children, allowed, redirectTo }) {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  if (!allowed(user)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return <NotAuthorized title="権限がありません" />;
  }
  return children;
}

/** Smart /settings index: send the user to a page they can actually access. */
export function SettingsIndexRedirect() {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <LoginRedirect />;
  if (canManageEmployees(user)) return <Navigate to="/settings/employees" replace />;
  return <Navigate to="/settings/signature" replace />;
}
